import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { NAVICA } from "@/lib/constants";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  usePageMeta({
    title: "Contact Navica Supports, Sydney NDIS Social Work",
    description: "Get in touch with Navica Supports. Phone 1300 105 034 or email info@navicasupports.com. Sydney-based, one business day response for participants, families and providers.",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: "Message received. We'll respond within one business day." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", message: err?.response?.data?.detail || "Couldn't send. Please try again or call us directly." });
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-16 lg:pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4">Connect with us</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1F2A2E] leading-tight mb-5">
            Let's <span className="italic" style={{ color: "var(--navica-coral)" }}>talk</span>.
          </h1>
          <p className="text-lg text-[#4A595E] leading-relaxed">
            Questions, referrals, partnership ideas or a no obligation chat. We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <div className="lg:col-span-5 space-y-5">
            <a href={`mailto:${NAVICA.email}`} className="block card-soft" data-testid="contact-email-card">
              <div className="w-12 h-12 rounded-xl bg-[#FCE7EB] flex items-center justify-center mb-4">
                <Mail className="w-5 h-5 text-[#E2718A]" />
              </div>
              <div className="text-xs uppercase tracking-wider text-[#6B7A80] mb-1">Email</div>
              <div className="font-display text-xl text-[#1F2A2E]">{NAVICA.email}</div>
            </a>
            <a href={`tel:${NAVICA.phoneRaw}`} className="block card-soft" data-testid="contact-phone-card">
              <div className="w-12 h-12 rounded-xl bg-[#FCEADB] flex items-center justify-center mb-4">
                <Phone className="w-5 h-5 text-[#EA823A]" />
              </div>
              <div className="text-xs uppercase tracking-wider text-[#6B7A80] mb-1">Phone</div>
              <div className="font-display text-xl text-[#1F2A2E]">{NAVICA.phone}</div>
            </a>
            <div className="card-soft" data-testid="contact-address-card">
              <div className="w-12 h-12 rounded-xl bg-[#DDE7E9] flex items-center justify-center mb-4">
                <MapPin className="w-5 h-5 text-[#284952]" />
              </div>
              <div className="text-xs uppercase tracking-wider text-[#6B7A80] mb-1">Office</div>
              <div className="font-display text-lg text-[#1F2A2E] leading-snug">{NAVICA.address}</div>
              <div className="text-sm text-[#6B7A80] mt-1">{NAVICA.city}</div>
            </div>
            <div className="card-soft" data-testid="contact-hours-card">
              <div className="w-12 h-12 rounded-xl bg-[#F1E4D5] flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-[#284952]" />
              </div>
              <div className="text-xs uppercase tracking-wider text-[#6B7A80] mb-1">Response time</div>
              <div className="font-display text-lg text-[#1F2A2E] leading-snug">Within 1 business day</div>
              <div className="text-sm text-[#6B7A80] mt-1">Monday to Friday, 9am to 5pm AEST</div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white rounded-[2.5rem] p-8 lg:p-10 border border-[#F0E5DE]" data-testid="contact-form">
              <h2 className="font-display text-3xl text-[#1F2A2E] mb-2">Send us a message</h2>
              <p className="text-sm text-[#6B7A80] mb-7">All fields marked with <span className="text-[#E2718A]">*</span> are required.</p>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#284952] mb-2">Your Name <span className="text-[#E2718A]">*</span></label>
                  <input className="input-navica" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="contact-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#284952] mb-2">Email <span className="text-[#E2718A]">*</span></label>
                    <input type="email" className="input-navica" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="contact-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#284952] mb-2">Phone</label>
                    <input type="tel" className="input-navica" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-phone" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#284952] mb-2">Subject</label>
                  <input className="input-navica" value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="e.g. NDIS support, partnership enquiry..." data-testid="contact-subject" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#284952] mb-2">Message <span className="text-[#E2718A]">*</span></label>
                  <textarea rows={6} className="input-navica" value={form.message} onChange={(e) => update("message", e.target.value)} required data-testid="contact-message" />
                </div>

                {status.state === "success" && (
                  <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex gap-3 items-start" data-testid="contact-success">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-emerald-800">{status.message}</span>
                  </div>
                )}
                {status.state === "error" && (
                  <div className="p-4 rounded-2xl bg-red-50 border border-red-200 flex gap-3 items-start" data-testid="contact-error">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-red-700">{status.message}</span>
                  </div>
                )}

                <button type="submit" disabled={status.state === "submitting"} className="btn-primary" data-testid="contact-submit">
                  {status.state === "submitting" ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
